/**
 * Viser alle produktkarakteristika med danske labels og prioriteret rækkefølge.
 */

const KEY_ALIASES = {
  køn: 'køn',
  kon: 'køn',
  gender: 'køn',
  sex: 'køn',
  farve: 'farve',
  color: 'farve',
  colour: 'farve',
  färg: 'farve',
  mærke: 'mærke',
  brand: 'mærke',
  rammebeskytter: 'rammebeskytter',
  'frame protector': 'rammebeskytter',
  'racket case': 'rammebeskytter',
  greb: 'greb_størrelse',
  'greb størrelse': 'greb_størrelse',
  'grip size': 'greb_størrelse',
  grip: 'greb_størrelse',
  overflade: 'overflade',
  surface: 'overflade',
  hårdhed: 'hårdhed',
  hardness: 'hårdhed',
  'bladhårdhed': 'hårdhed',
  'bladhårdhet': 'hårdhed',
  skumhårdhed: 'hårdhed',
  balance: 'balance',
  balancepunkt: 'balance',
  'balance point': 'balance',
  struktur: 'struktur',
  structure: 'struktur',
  mønster: 'struktur',
  vægt: 'vægt',
  weight: 'vægt',
  vaegt: 'vægt',
  vikt: 'vægt',
  spillestil: 'spillestil',
  'play style': 'spillestil',
  'playing style': 'spillestil',
  style: 'spillestil',
  niveau: 'niveau',
  level: 'niveau',
  form: 'form',
  shape: 'form',
  hovedform: 'form',
  'head shape': 'form',
  kerne: 'kerne',
  core: 'kerne',
  skum: 'kerne',
  foam: 'kerne',
  'inner core': 'kerne',
  'outer core': 'kerne',
  materiale: 'materiale',
  material: 'materiale',
  ramme: 'ramme',
  frame: 'ramme',
  profil: 'profil',
  profile: 'profil',
  tykkelse: 'tykkelse',
  thickness: 'tykkelse',
  længde: 'længde',
  length: 'længde',
  bredde: 'bredde',
  width: 'bredde',
  størrelse: 'størrelse',
  size: 'størrelse',
  årgang: 'år',
  year: 'år',
  model: 'model',
  serie: 'serie',
  'product line': 'serie',
  fattning: 'fattning',
  'control/power': 'spillestil',
}

const LABELS = {
  køn: 'Køn',
  farve: 'Farve',
  mærke: 'Mærke',
  rammebeskytter: 'Rammebeskytter',
  greb_størrelse: 'Greb størrelse',
  overflade: 'Overflade',
  hårdhed: 'Hårdhed',
  balance: 'Balance',
  struktur: 'Struktur',
  vægt: 'Vægt',
  spillestil: 'Spillestil',
  niveau: 'Niveau',
  form: 'Form',
  kerne: 'Kerne',
  materiale: 'Materiale',
  ramme: 'Ramme',
  profil: 'Profil',
  tykkelse: 'Tykkelse',
  længde: 'Længde',
  bredde: 'Bredde',
  størrelse: 'Størrelse',
  år: 'Årgang',
  model: 'Model',
  serie: 'Serie',
  fattning: 'Fattning',
}

/** Prioriteret visningsrækkefølge */
const DISPLAY_ORDER = [
  'køn',
  'farve',
  'mærke',
  'år',
  'model',
  'serie',
  'rammebeskytter',
  'greb_størrelse',
  'overflade',
  'hårdhed',
  'balance',
  'struktur',
  'vægt',
  'spillestil',
  'niveau',
  'form',
  'kerne',
  'materiale',
  'ramme',
  'profil',
  'tykkelse',
  'længde',
  'bredde',
  'størrelse',
  'fattning',
]

function normalizeSpecKey(key) {
  const raw = String(key || '')
    .toLowerCase()
    .trim()
    .replace(/[:]+$/, '')
  return KEY_ALIASES[raw] || raw
}

function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function getSpecLabel(key) {
  const canon = normalizeSpecKey(key)
  return LABELS[canon] || humanizeKey(canon)
}

function isEmptyValue(value) {
  if (value == null) return true
  const s = String(value).trim()
  return !s || s === '-' || s === 'n/a'
}

/**
 * @returns {{ key: string, label: string, value: string }[]}
 */
export function getProductCharacteristics(product) {
  if (!product) return []

  const byKey = new Map()

  const add = (rawKey, value, labelOverride) => {
    if (isEmptyValue(value)) return
    const canon = normalizeSpecKey(rawKey)
    if (byKey.has(canon)) return
    byKey.set(canon, {
      key: canon,
      label: labelOverride || getSpecLabel(canon),
      value: String(value).trim(),
    })
  }

  if (product.brand) add('mærke', product.brand, 'Mærke')
  if (product.year) add('år', product.year, 'Årgang')
  if (product.model) add('model', product.model, 'Model')

  const specs = product.specifications || {}
  for (const [k, v] of Object.entries(specs)) {
    if (k === 'brand' || k === 'mærke') continue
    add(k, v)
  }

  const rows = [...byKey.values()]
  const orderIndex = (key) => {
    const i = DISPLAY_ORDER.indexOf(key)
    return i === -1 ? 999 : i
  }

  rows.sort((a, b) => {
    const oa = orderIndex(a.key)
    const ob = orderIndex(b.key)
    if (oa !== ob) return oa - ob
    return a.label.localeCompare(b.label, 'da')
  })

  return rows
}
